import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreateInteraction } from "@/api/hooks/useInteractions";
import {
  INTERACTION_DIRECTION_LABELS,
  INTERACTION_TYPE_LABELS,
  InteractionDirectionSchema,
  InteractionTypeSchema,
  type InteractionDirection,
  type InteractionType,
} from "@/api/types/interaction";

const TYPES = InteractionTypeSchema.options;
const DIRECTIONS = InteractionDirectionSchema.options;

interface LogInteractionDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
}

/** "Local datetime input value" (no timezone) -> ISO string for the API. */
function toIsoOrUndefined(localValue: string): string | undefined {
  if (!localValue) return undefined;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function LogInteractionDialog({ open, onClose, contactId }: LogInteractionDialogProps) {
  const [type, setType] = useState<InteractionType>("note");
  const [direction, setDirection] = useState<InteractionDirection>("na");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState("");

  const createInteraction = useCreateInteraction();

  useEffect(() => {
    if (open) {
      setType("note");
      setDirection("na");
      setSubject("");
      setBody("");
      setOccurredAt("");
    }
  }, [open]);

  const canSubmit = !createInteraction.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createInteraction.mutateAsync({
        contactId,
        type,
        direction,
        subject: subject.trim() || undefined,
        body: body.trim() || undefined,
        occurred_at: toIsoOrUndefined(occurredAt),
      });
      toastSuccess("Interaction logged");
      onClose();
    } catch (err) {
      toastError("Couldn't log interaction", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Log interaction">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Log interaction</DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormSelect
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as InteractionType)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {INTERACTION_TYPE_LABELS[t]}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              label="Direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as InteractionDirection)}
            >
              {DIRECTIONS.map((d) => (
                <option key={d} value={d}>
                  {INTERACTION_DIRECTION_LABELS[d]}
                </option>
              ))}
            </FormSelect>
          </div>

          <FormField
            label="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <FormTextarea
            label="Notes (optional)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />

          <FormField
            label="When (optional)"
            type="datetime-local"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
            hint="Leave blank to use now."
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createInteraction.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createInteraction.isPending ? "Saving..." : "Log interaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
